'use client';
import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/src/store/store';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { closeDrawer } from '@/src/store/slices/allDrawerSlice';
import { ChevronDownSVG } from '@/src/ui/AllSVG';
import { Button, Slider } from '@heroui/react';
import { useGetAllBrandsDataQuery, useGetAllFiltersDataQuery, useGetGlobalSettingsQuery } from '@/src/store/rtkQueries/userGetAPI';
import ChipsSkeleton from '@/src/ui/skeletons/ChipsSkeleton';
import { formatAmount } from '@/src/utils/formatters';

const SORT_OPTIONS = [
    { label: 'DEFAULT', value: 'DEFAULT' },
    { label: 'NEW ARRIVALS', value: 'new_arrival' },
    { label: 'BEST SELLERS', value: 'best_seller' },
    { label: 'PRICE LOW TO HIGH', value: 'price_low_to_high' },
    { label: 'PRICE HIGH TO LOW', value: 'price_high_to_low' },
];

/** Slider bounds; full range means no price filter (URL omits priceMin/priceMax). */
const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 1000;

function priceRangeFromSearchParams(searchParams: URLSearchParams): [number, number] {
    const rawMin = searchParams.get('priceMin');
    const rawMax = searchParams.get('priceMax');
    const min = rawMin != null && rawMin !== '' ? Number(rawMin) : PRICE_SLIDER_MIN;
    const max = rawMax != null && rawMax !== '' ? Number(rawMax) : PRICE_SLIDER_MAX;
    return [min, max];
}


function SectionHeader({ title, isOpen, onToggle }: { title: string; isOpen: boolean; onToggle: () => void }) {
    return (
        <button
            onClick={onToggle}
            className="flex justify-between items-center w-full py-4 border-t border-muted-color"
        >
            <span className="text-sm uppercase">{title}</span>
            <ChevronDownSVG className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
    );
}

function Chip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <Button
            onClick={onClick}
            className={`global_btn capitalize min-w-fit! text-xs!  ${selected ? 'bg_primary' : 'outline_secondary'}`}
        >
            {label}
        </Button>
    );
}

export default function FilterSortDrawer() {
    const dispatch = useDispatch();
    const { data } = useSelector((state: RootState) => state.allCommonDrawer)
    const resultCount = data?.totalCount ?? 0;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { data: filtersData, isLoading: isFiltersLoading } = useGetAllFiltersDataQuery();
    const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrandsDataQuery();
    const { data: globalSettings } = useGetGlobalSettingsQuery();
    const currency = globalSettings?.data?.currency ?? '';
    const COLORS = filtersData?.data?.colors ?? [];
    const SIZES = filtersData?.data?.sizes ?? [];
    const BRANDS = brandsData?.data ?? [];

    const [selectedSort, setSelectedSort] = useState<string>(
        () => searchParams.get('sort') ?? 'DEFAULT'
    );
    const [priceRange, setPriceRange] = useState<[number, number]>(() =>
        priceRangeFromSearchParams(searchParams)
    );
    const [selectedSizes, setSelectedSizes] = useState<string[]>(
        () => searchParams.get('size')?.split(',').filter(Boolean) ?? []
    );
    const [selectedColors, setSelectedColors] = useState<string[]>(
        () => searchParams.get('color')?.split(',').filter(Boolean) ?? []
    );
    // const [selectedStyles, setSelectedStyles] = useState<string[]>(
    //     () => searchParams.get('style')?.split(',').filter(Boolean) ?? []
    // );
    const [selectedBrands, setSelectedBrands] = useState<string[]>(
        () => searchParams.get('brand')?.split(',').filter(Boolean) ?? []
    );
    const [isExclusive, setIsExclusive] = useState<boolean>(
        () => searchParams.get('is_exclusive') === 'true'
    );
    // const [selectedFits, setSelectedFits] = useState<string[]>(
    //     () => searchParams.get('fit')?.split(',').filter(Boolean) ?? []
    // );

    const [openSections, setOpenSections] = useState({
        price: false,
        size: false,
        color: false,
        exclusive: false,
        // style: false,
        brands: false,
        // fit: false,
    });

    const toggleSection = (key: keyof typeof openSections) => {
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleItem = (item: string, list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>) => {
        setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
    };

    const handlePriceChange = useCallback((val: number | number[]) => {
        setPriceRange(val as [number, number]);
    }, []);

    const handleDone = () => {
        const params = new URLSearchParams(searchParams.toString());

        if (selectedSort === 'DEFAULT') params.delete('sort');
        else params.set('sort', selectedSort);

        if (priceRange[0] === PRICE_SLIDER_MIN && priceRange[1] === PRICE_SLIDER_MAX) {
            params.delete('priceMin');
            params.delete('priceMax');
        } else {
            params.set('priceMin', String(priceRange[0]));
            params.set('priceMax', String(priceRange[1]));
        }

        if (selectedSizes.length) params.set('size', selectedSizes.join(','));
        else params.delete('size');

        if (selectedColors.length) params.set('color', selectedColors.join(','));
        else params.delete('color');

        if (selectedBrands.length) params.set('brand', selectedBrands.join(','));
        else params.delete('brand');

        if (isExclusive) params.set('is_exclusive', 'true');
        else params.delete('is_exclusive');

        const query = params.toString();
        router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
        dispatch(closeDrawer());
    };

    return (
        <>
            <div className="flex flex-col h-full text-body-color">
                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto custom_scrollbar pb-4 ps-1">
                    {/* Sort By */}
                    <div className="pt-5 pb-2">
                        <p className="text-sm uppercase mb-3">Sort By</p>
                        <div className="flex flex-wrap gap-2">
                            {SORT_OPTIONS.map(opt => (
                                <Chip
                                    key={opt.value}
                                    label={opt.label}
                                    selected={selectedSort === opt.value}
                                    onClick={() => setSelectedSort(opt.value)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Price Range */}
                    <SectionHeader title="Price Range" isOpen={openSections.price} onToggle={() => toggleSection('price')} />
                    {openSections.price && (
                        <div className="pb-4">
                            <Slider
                                className="w-full"
                                aria-label="Price Range"
                                value={priceRange}
                                onChange={handlePriceChange}
                                formatOptions={currency ? { currency, style: "currency" } : undefined}
                                maxValue={PRICE_SLIDER_MAX}
                                minValue={PRICE_SLIDER_MIN}
                                step={50}
                            >
                                <Slider.Track className="relative w-full h-1 bg-muted-color rounded-full">
                                    <Slider.Fill className="absolute h-full bg-body-color rounded-full" />
                                    <Slider.Thumb index={0} className="w-6 h-6 rounded-full bg-white border-4 border-body-color shadow-sm top-1/2 cursor-grab" />
                                    <Slider.Thumb index={1} className="w-6 h-6 rounded-full bg-white border-4 border-body-color shadow-sm top-1/2 cursor-grab" />
                                </Slider.Track>
                            </Slider>
                            {/* Min / Max inputs */}
                            <div className="flex justify-between gap-4 mt-4">
                                <div className="flex-1">
                                    <p className="text-xs text-black/70 mb-1">Minimum</p>
                                    <Button className="global_btn min-w-fit! text-xs! outline_secondary">
                                        {formatAmount(priceRange[0], currency)}
                                    </Button>
                                </div>
                                <div className="flex-1 text-right">
                                    <p className="text-xs text-black/70 mb-1 text-right">Maximum</p>
                                    <Button className="global_btn min-w-fit! text-xs! outline_secondary">
                                        {formatAmount(priceRange[1], currency)}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Size */}
                    <SectionHeader title="Size" isOpen={openSections.size} onToggle={() => toggleSection('size')} />
                    {openSections.size && (
                        isFiltersLoading
                            ? <ChipsSkeleton count={8} />
                            : (
                                <div className="flex flex-wrap gap-2 pb-4">
                                    {SIZES && SIZES?.length > 0 && SIZES?.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item?.name}
                                            selected={selectedSizes.includes(item?.name)}
                                            onClick={() => toggleItem(item?.name, selectedSizes, setSelectedSizes)}
                                        />
                                    ))}
                                </div>
                            )
                    )}

                    {/* Colour */}
                    <SectionHeader title="Color" isOpen={openSections.color} onToggle={() => toggleSection('color')} />
                    {openSections.color && (
                        isFiltersLoading
                            ? <ChipsSkeleton count={8} withSwatch />
                            : (
                                <div className="flex flex-wrap gap-x-4 gap-y-3 pb-4">
                                    {COLORS && COLORS?.length > 0 && COLORS?.map((item, index) => {
                                        const selected = selectedColors.includes(item?.name);
                                        return (
                                            <Button
                                                key={index}
                                                onClick={() => toggleItem(item?.name, selectedColors, setSelectedColors)}
                                                className={`global_btn capitalize min-w-fit! text-xs! px-4! py-0! ${selected ? 'bg_primary' : 'outline_secondary'}`}
                                            >
                                                <span
                                                    className={`w-4 h-4 rounded-full shrink-0 ${selected ? ' border-2 border-black' : 'border-transparent shadow-sm!'}`}
                                                    style={{ backgroundColor: item?.code, boxShadow: `0 0 0 1px ${item?.code}` }}
                                                />
                                                {item?.name}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )
                    )}

                    {/* Exclusive */}
                    <SectionHeader title="Exclusive" isOpen={openSections.exclusive} onToggle={() => toggleSection('exclusive')} />
                    {openSections.exclusive && (
                        <div className="flex flex-wrap gap-2 pb-4">
                            <Chip
                                label="All products"
                                selected={!isExclusive}
                                onClick={() => setIsExclusive(false)}
                            />
                            <Chip
                                label="Exclusive only"
                                selected={isExclusive}
                                onClick={() => setIsExclusive(true)}
                            />
                        </div>
                    )}

                    {/* Style */}
                    {/* <SectionHeader title="Style" isOpen={openSections.style} onToggle={() => toggleSection('style')} />
                    {openSections.style && (
                        <div className="flex flex-wrap gap-2 pb-4">
                            {STYLES.map(s => (
                                <Chip
                                    key={s}
                                    label={s}
                                    selected={selectedStyles.includes(s)}
                                    onClick={() => toggleItem(s, selectedStyles, setSelectedStyles)}
                                />
                            ))}
                        </div>
                    )} */}

                    {/* Brands */}
                    <SectionHeader title="Brands" isOpen={openSections.brands} onToggle={() => toggleSection('brands')} />
                    {openSections.brands && (
                        isBrandsLoading
                            ? <ChipsSkeleton count={6} />
                            : (
                                <div className="flex flex-wrap gap-2 pb-4">
                                    {BRANDS && BRANDS?.length > 0 && BRANDS?.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item?.name}
                                            selected={selectedBrands.includes(item?.slug)}
                                            onClick={() => toggleItem(item?.slug, selectedBrands, setSelectedBrands)}
                                        />
                                    ))}
                                </div>
                            )
                    )}

                    {/* Fit */}
                    {/* <SectionHeader title="Fit" isOpen={openSections.fit} onToggle={() => toggleSection('fit')} />
                    {openSections.fit && (
                        <div className="flex flex-wrap gap-2 pb-4">
                            {FITS.map(f => (
                                <Chip
                                    key={f}
                                    label={f}
                                    selected={selectedFits.includes(f)}
                                    onClick={() => toggleItem(f, selectedFits, setSelectedFits)}
                                />
                            ))}
                        </div>
                    )} */}
                </div>

                {/* Done button */}
                <div className="py-4 border-t border-muted-color">
                    <Button
                        onClick={handleDone}
                        className={`global_btn w-full uppercase text-sm tracking-[0.6px]`}                    >
                        Done
                    </Button>
                </div>
            </div>
        </>
    );
}
